import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faTimes,
  faSpinner,
  faUserShield
} from "@fortawesome/free-solid-svg-icons";
import { jwtDecode } from "jwt-decode";
import userApi from "../../services/api/userApi";
import useAppSession from "../../stores/useAppSession.js";


const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 40px 24px;
  background-color: #f2f2f2;
  min-height: 100vh;
  box-sizing: border-box;
`;

const MainCard = styled.div`
  width: 100%;
  max-width: 1100px;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 32px;
  box-sizing: border-box;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #eaeaea;
  border-radius: 6px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  background: #fff;

  th {
    text-align: left;
    padding: 14px;
    background: #fafafa;
    border-bottom: 1px solid #eaeaea;
    color: #333;
    font-weight: 600;
  }

  td {
    padding: 14px;
    border-bottom: 1px solid #eaeaea;
    color: #666;
  }
`;

const PrimaryButton = styled.button`
  background-color: #ff8f40;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s;

  &:hover { opacity: 0.9; }
`;

const IconButton = styled.button`
  background: ${props => props.danger ? "#fff1f0" : "#f5f5f5"};
  color: ${props => props.danger ? "#ff4d4f" : "#666"};
  border: 1px solid ${props => props.danger ? "#ffa39e" : "#eaeaea"};
  border-radius: 6px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.danger ? "#ff4d4f" : "#ff8f40"};
    color: #fff;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 100%;
  max-width: 500px;
  padding: 32px;
  max-height: 90vh;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  label {
    display: block;
    font-size: 15px;
    font-weight: 500;
    margin-bottom: 8px;
    color: #333;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  font-size: 15px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #ff8f40; }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  font-size: 15px;
  background: white;
  outline: none;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f0f0f0;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 13px;
  margin-right: 6px;
  margin-top: 6px;
  color: #333;
  border: 1px solid #ddd;
  
  svg {
    cursor: pointer;
    color: #999;
    &:hover { color: #ff4d4f; }
  }
`;


function decode(token) {
  try { return jwtDecode(token); } catch { return null; }
}

const AVAILABLE_ROLES = ["ADMIN_IA", "ACC", "ADMIN", "USER"];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [environments, setEnvironments] = useState([]);

  const [formData, setFormData] = useState({
    name: "", login: "", password: "", confirmPassword: "", roles: [], environment_id: ""
  });

  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [selectedRole, setSelectedRole] = useState("");

  const { token } = useAppSession();

  const validatePassword = (password) => {
    if (!password) return "";
    if (password.length < 8) return "Mínimo de 8 caracteres";
    if (!/[A-Z]/.test(password)) return "Mínimo 1 letra maiúscula";
    if (!/[a-z]/.test(password)) return "Mínimo 1 letra minúscula";
    if (!/[0-9]/.test(password)) return "Mínimo 1 número";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return "Mínimo 1 caractere especial";
    return "";
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await userApi.getUsersList();
      setUsers(Array.isArray(res.data.users) ? res.data.users : []);
    } catch {
      toast.error("Erro ao buscar usuários");
    } finally {
      setLoading(false);
    }
  };

  const fetchEnvironments = async () => {
    try {
      const res = await userApi.getEnvironments();
      setEnvironments(res.data.environments || []);
    } catch (err) { console.error(err); }
  };
  

  useEffect(() => {
    fetchUsers();
    fetchEnvironments();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editingUser && (passwordError || confirmError)) {
      toast.error("Verifique os erros na senha");
      return;
    }

    try {
      if (editingUser) {
        await userApi.updateProfile(editingUser.userid, {
          nome: formData.name,
          senha: formData.password
        });
        toast.success("Usuário atualizado!");
      } else {
        const decodedToken = decode(token);
        const res = await userApi.createNewUser(
          decodedToken.user_access.environment.workspace.name,
          formData.login,
          formData.name,
          formData.password,
          formData.roles.join(",")
        );

        if (res.data.userid && formData.environment_id) {
          await userApi.createAccess({
            keyspace_name: decodedToken.user_access.environment.workspace.name,
            user_id: res.data.userid,
            environment_id: formData.environment_id,
            isadmin: false
          });
        }
        toast.success("Usuário criado!");
      }
      setIsModalVisible(false);
      fetchUsers();
    } catch {
      toast.error("Erro ao salvar usuário");
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Deseja realmente excluir este usuário?")) {
      try {
        await userApi.deleteUser(userId);
        toast.success("Excluído com sucesso!");
        fetchUsers();
      } catch {
        toast.error("Erro ao excluir");
      }
    }
  };

  const addRole = () => {
    if (selectedRole && !formData.roles.includes(selectedRole)) {
      setFormData(prev => ({ ...prev, roles: [...prev.roles, selectedRole] }));
      setSelectedRole("");
    }
  };

  return (
    <PageWrapper>
      <MainCard>
        <HeaderActions>
          <Title>Gerenciamento de Usuários</Title>
          <PrimaryButton onClick={() => {
            setEditingUser(null);
            setFormData({ name: "", login: "", password: "", confirmPassword: "", roles: [], environment_id: "" });
            setPasswordError("");
            setConfirmError("");
            setIsModalVisible(true);
          }}>
            <FontAwesomeIcon icon={faPlus} /> Novo Usuário
          </PrimaryButton>
        </HeaderActions>

        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Login</th>
                <th>Permissões</th>
                <th style={{ textAlign: "center" }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" style={{ textAlign: "center", padding: "40px" }}><FontAwesomeIcon icon={faSpinner} spin size="2x" color="#ff8f40" /></td></tr>
              ) : (
                users.map((user) => (
                  <tr key={user.userid}>
                    <td>{user.name}</td>
                    <td>{user.login}</td>
                    <td>{Array.isArray(user.roles) ? user.roles.join(", ") : user.roles}</td>
                    <td>
                      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <IconButton onClick={() => {
                          setEditingUser(user);
                          setFormData({ ...formData, name: user.name, login: user.login });
                          setIsModalVisible(true);
                        }}>
                          <FontAwesomeIcon icon={faEdit} />
                        </IconButton>
                        <IconButton danger onClick={() => handleDelete(user.userid)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </StyledTable>
        </TableContainer>
      </MainCard>

      {isModalVisible && (
        <ModalOverlay>
          <ModalContent>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "24px" }}>
              <Title style={{ margin: 0 }}>{editingUser ? "Editar" : "Novo"} Usuário</Title>
              <FontAwesomeIcon icon={faTimes} style={{ cursor: "pointer", color: "#999" }} onClick={() => setIsModalVisible(false)} />
            </div>

            <form onSubmit={handleSave}>
              <FormGroup>
                <label>Nome Completo</label>
                <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </FormGroup>

              {!editingUser && (
                <>
                  <FormGroup>
                    <label>E-mail (Login)</label>
                    <Input required type="email" value={formData.login} onChange={(e) => setFormData({ ...formData, login: e.target.value })} />
                  </FormGroup>

                  <FormGroup>
                    <label>Ambiente (Environment)</label>
                    <Select required value={formData.environment_id} onChange={(e) => setFormData({ ...formData, environment_id: e.target.value })}>
                      <option value="">Selecione um ambiente...</option>
                      {environments.map((env) => <option key={env.environmentid} value={env.environmentid}>{env.name}</option>)}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <label>Atribuir Permissões</label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                        <option value="">Selecionar Role...</option>
                        {AVAILABLE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </Select>
                      <PrimaryButton type="button" onClick={addRole}><FontAwesomeIcon icon={faUserShield} /></PrimaryButton>
                    </div>
                    <div>
                      {formData.roles.map((role) => (
                        <Badge key={role}>
                          {role} <FontAwesomeIcon icon={faTimes} onClick={() => setFormData({ ...formData, roles: formData.roles.filter(r => r !== role) })} />
                        </Badge>
                      ))}
                    </div>
                  </FormGroup>
                </>
              )}

              <FormGroup>
                <label>{editingUser ? "Nova Senha (deixe em branco para manter)" : "Senha"}</label>
                <Input
                  type="password"
                  required={!editingUser}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    setPasswordError(validatePassword(e.target.value));
                  }}
                />
                {passwordError && <small style={{ color: "#ff4d4f", marginTop: "4px", display: "block" }}>{passwordError}</small>}
              </FormGroup>

              <FormGroup>
                <label>Confirmar Senha</label>
                <Input
                  type="password"
                  required={!editingUser}
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, confirmPassword: e.target.value });
                    setConfirmError(e.target.value !== formData.password ? "As senhas não conferem" : "");
                  }}
                />
                {confirmError && <small style={{ color: "#ff4d4f", marginTop: "4px", display: "block" }}>{confirmError}</small>}
              </FormGroup>

              <div style={{ display: "flex", gap: "12px", marginTop: "32px" }}>
                <PrimaryButton style={{ flex: 1 }} type="submit">Salvar Usuário</PrimaryButton>
                <PrimaryButton style={{ flex: 1, background: "#f5f5f5", color: "#333" }} type="button" onClick={() => setIsModalVisible(false)}>Cancelar</PrimaryButton>
              </div>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageWrapper>
  );
};

export default UserManagement;